import React, { useState } from "react";
import {
  Breadcrumb,
  Layout,
  theme,
  Card,
  Avatar,
  Typography,
  Button,
  Tabs,
  Form,
  Input,
  Row,
  Col,
  Divider,
  Space,
  Tag,
  List,
  Switch,
  message,
  Modal
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  BellOutlined,
  SafetyCertificateOutlined,
  HistoryOutlined,
  LogoutOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  IdcardOutlined,
  TeamOutlined
} from "@ant-design/icons";
import { useAuth } from "../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { UserRoleEnum } from "../enums/UserRoleEnum";
import { GeneralStatusEnum } from "../enums/GeneralStatusEnum";
import styled from "styled-components";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

// Styled components
const StyledContent = styled(Content)`
  margin: 0 24px;
  
  @media (max-width: 768px) {
    margin: 0 16px;
  }
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin: 24px 0 16px 0;
  
  .ant-breadcrumb-link {
    color: #666;
    
    &:hover {
      color: #4169e1;
    }
  }
  
  a {
    color: #4169e1;
    text-decoration: none;
    
    &:hover {
      color: #1890ff;
    }
  }
`;

const ContentWrapper = styled.div`
  padding: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ProfileCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  text-align: center;
  height: 100%;
  
  .ant-card-body {
    padding: 32px;
    display: flex;
    flex-direction: column;
    height: 100%;
    
    @media (max-width: 768px) {
      padding: 24px;
    }
  }
`;

const StyledAvatar = styled(Avatar)`
  background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%);
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(65, 105, 225, 0.3);
`;

const StyledTitle = styled(Title)`
  color: #1a2847 !important;
  margin-bottom: 8px !important;
`;

const InfoText = styled(Text)`
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  
  .anticon {
    margin-right: 8px;
    color: #4169e1;
  }
`;

const StyledButton = styled(Button)`
  &.ant-btn-primary {
    background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%);
    border: none;
    height: 40px;
    padding: 0 24px;
    font-size: 15px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(65, 105, 225, 0.3);
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(65, 105, 225, 0.4);
    }
  }
  
  &.ant-btn-default {
    height: 40px;
    padding: 0 20px;
    border-color: #e6e6e6;
    
    &:hover {
      border-color: #4169e1;
      color: #4169e1;
    }
  }
  
  &.ant-btn-dangerous {
    height: 40px;
    padding: 0 20px;
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
    
    .ant-tabs-tab {
      padding: 12px 0;
      font-size: 15px;
      
      &.ant-tabs-tab-active {
        color: #4169e1;
      }
      
      &:hover {
        color: #4169e1;
      }
    }
    
    .ant-tabs-ink-bar {
      background: linear-gradient(90deg, #4169e1 0%, #1890ff 100%);
    }
  }
`;

const TabCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  
  .ant-card-body {
    padding: 32px;
    
    @media (max-width: 768px) {
      padding: 20px;
    }
  }
`;

const SectionTitle = styled(Title)`
  color: #1a2847 !important;
  margin-bottom: 24px !important;
  font-size: 20px !important;
`;

const StyledTag = styled(Tag)`
  margin: 0 4px 8px 0;
  padding: 4px 12px;
  font-size: 14px;
  border-radius: 6px;
  
  &.ant-tag-blue {
    background: #e6f0ff;
    border-color: #4169e1;
    color: #4169e1;
  }
`;

const StyledList = styled(List)`
  .ant-list-item {
    padding: 16px 0;
    border-bottom: 1px solid #f0f0f0;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .ant-list-item-meta-title {
    color: #1a2847;
    font-weight: 500;
    margin-bottom: 4px;
  }
  
  .ant-list-item-meta-description {
    color: #666;
    font-size: 14px;
  }
  
  .ant-switch {
    &.ant-switch-checked {
      background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%);
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: 1px solid #f0f0f0;
  }
  
  .ant-modal-title {
    color: #1a2847;
    font-size: 18px;
    font-weight: 600;
  }
  
  .ant-modal-content {
    border-radius: 12px;
  }
`;

const StyledDivider = styled(Divider)`
  margin: 24px 0;
  border-color: #f0f0f0;
`;

const ActivityItem = styled.div`
  .activity-action {
    color: #1a2847;
    font-weight: 500;
    margin-bottom: 4px;
  }
  
  .activity-details {
    color: #666;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    color: #1a2847;
    font-weight: 500;
  }
  
  .ant-input,
  .ant-input-password {
    height: 40px;
    border-radius: 8px;
    border-color: #e6e6e6;
    
    &:hover,
    &:focus {
      border-color: #4169e1;
    }
  }
  
  .ant-input-password-icon {
    color: #8c8c8c;
    
    &:hover {
      color: #4169e1;
    }
  }
`;

interface IProfileFormValues {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
}

interface IPasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface INotificationSetting {
  key: string;
  title: string;
  description: string;
  defaultChecked: boolean;
}

interface ISecuritySetting {
  key: string;
  title: string;
  description: string;
  defaultChecked: boolean;
}

interface IDeviceInfo {
  name: string;
  lastActive: string;
  current: boolean;
}

interface IActivityLog {
  date: string;
  action: string;
  details: string;
  ip: string;
}

const ProfilePage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const [profileForm] = Form.useForm<IProfileFormValues>();
  const [passwordForm] = Form.useForm<IPasswordFormValues>();
  const [editingProfile, setEditingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Get user's first name, last name, and role descriptions
  const firstName = user?.firstName || user?.email?.split('@')[0] || 'User';
  const lastName = user?.lastName || '';
  const email = user?.email || '';
  
  // Get formatted role names
  const getRoleNames = () => {
    if (!user?.roles) return [];
    
    // Handle the case where roles is a string
    const rolesArray = typeof user.roles === 'string' 
      ? user.roles.split(',') 
      : Array.isArray(user.roles) 
        ? user.roles 
        : [];
    
    return rolesArray.map((role: string) => {
      // Trim any whitespace
      const roleValue = role.trim();
      
      switch(roleValue) {
        case UserRoleEnum.SuperAdmin:
          return intl.formatMessage({ id: 'profile.role.superAdmin' }) || 'Super Admin';
        case UserRoleEnum.OrganisationAdmin:
          return intl.formatMessage({ id: 'profile.role.orgAdmin' }) || 'Organisation Admin';
        case UserRoleEnum.OrganisationViewer:
          return intl.formatMessage({ id: 'profile.role.orgViewer' }) || 'Organisation Viewer';
        default:
          return roleValue || intl.formatMessage({ id: 'profile.role.user' }) || 'User';
      }
    });
  };

  // Get account status color
  const getStatusColor = () => {
    if (!user?.status) return 'default';
    
    // Ensure we're comparing strings
    const status = String(user.status).trim();
    
    switch(status) {
      case GeneralStatusEnum.Active:
        return 'success';
      case GeneralStatusEnum.Suspended:
        return 'warning';
      case GeneralStatusEnum.Deleted:
        return 'error';
      default:
        return 'default';
    }
  };

  // Get account status text
  const getStatusText = () => {
    if (!user?.status) return 'Unknown';
    
    // Ensure we're comparing strings
    const status = String(user.status).trim();
    
    switch(status) {
      case GeneralStatusEnum.Active:
        return intl.formatMessage({ id: 'profile.status.active' }) || 'Active';
      case GeneralStatusEnum.Suspended:
        return intl.formatMessage({ id: 'profile.status.suspended' }) || 'Suspended';
      case GeneralStatusEnum.Deleted:
        return intl.formatMessage({ id: 'profile.status.deleted' }) || 'Deleted';
      default:
        // Default to showing active if we don't recognize the status
        return status === 'ACTIVE' || status.toLowerCase() === 'active' 
          ? intl.formatMessage({ id: 'profile.status.active' }) || 'Active'
          : status;
    }
  };

  // Start editing profile
  const handleEditProfile = () => {
    profileForm.setFieldsValue({
      firstName,
      lastName,
      email,
      jobTitle: user?.jobTitle || ''
    });
    setEditingProfile(true);
  };

  // Cancel editing profile
  const handleCancelEdit = () => {
    setEditingProfile(false);
  };

  // Submit profile changes
  const handleProfileSubmit = (values: unknown) => {
    const typedValues = values as IProfileFormValues;
    console.log('Profile form values:', typedValues);
    
    // In a real implementation, you would call an API to update the user profile
    // For now, we'll just show a success message
    messageApi.success(intl.formatMessage({ id: 'profile.updateSuccess' }) || 'Profile updated successfully');
    setEditingProfile(false);
  };

  // Start changing password
  const handleChangePassword = () => {
    passwordForm.resetFields();
    setChangingPassword(true);
  };

  // Cancel password change
  const handleCancelPasswordChange = () => {
    setChangingPassword(false);
  };

  // Submit password change
  const handlePasswordSubmit = (values: unknown) => {
    const typedValues = values as IPasswordFormValues;
    console.log('Password form values:', typedValues);
    
    // In a real implementation, you would call an API to update the password
    // For now, we'll just show a success message
    messageApi.success(intl.formatMessage({ id: 'profile.passwordUpdateSuccess' }) || 'Password updated successfully');
    setChangingPassword(false);
  };

  // Handle sign out
  const handleSignOut = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'profile.signOut.confirm' }) || 'Sign Out',
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'profile.signOut.message' }) || 'Are you sure you want to sign out?',
      onOk: () => {
        logout();
      }
    });
  };

  // Mock notification settings
  const notificationSettings: INotificationSetting[] = [
    {
      key: 'email_notifications',
      title: intl.formatMessage({ id: 'profile.notifications.email' }) || 'Email Notifications',
      description: intl.formatMessage({ id: 'profile.notifications.emailDesc' }) || 'Receive email notifications for important updates',
      defaultChecked: true
    },
    {
      key: 'event_reminders',
      title: intl.formatMessage({ id: 'profile.notifications.eventReminders' }) || 'Event Reminders',
      description: intl.formatMessage({ id: 'profile.notifications.eventRemindersDesc' }) || 'Receive reminders about upcoming events',
      defaultChecked: true
    },
    {
      key: 'new_photos',
      title: intl.formatMessage({ id: 'profile.notifications.newPhotos' }) || 'New Photos',
      description: intl.formatMessage({ id: 'profile.notifications.newPhotosDesc' }) || 'Get notified when new photos are added to your events',
      defaultChecked: true
    },
    {
      key: 'marketing',
      title: intl.formatMessage({ id: 'profile.notifications.marketing' }) || 'Marketing Updates',
      description: intl.formatMessage({ id: 'profile.notifications.marketingDesc' }) || 'Receive promotional content and feature updates',
      defaultChecked: false
    }
  ];

  // Mock security settings
  const securitySettings: ISecuritySetting[] = [
    {
      key: 'two_factor',
      title: intl.formatMessage({ id: 'profile.security.twoFactor' }) || 'Two-Factor Authentication',
      description: intl.formatMessage({ id: 'profile.security.twoFactorDesc' }) || 'Add an extra layer of security to your account',
      defaultChecked: false
    },
    {
      key: 'login_alerts',
      title: intl.formatMessage({ id: 'profile.security.loginAlerts' }) || 'Login Alerts',
      description: intl.formatMessage({ id: 'profile.security.loginAlertsDesc' }) || 'Get notified of new or unusual login activity',
      defaultChecked: true
    }
  ];

  // Mock activity logs
  const activityLogs: IActivityLog[] = [
    {
      date: '2025-05-05',
      action: intl.formatMessage({ id: 'profile.activity.login' }) || 'Logged in',
      details: 'Chrome on Windows',
      ip: '192.168.1.1'
    },
    {
      date: '2025-05-04',
      action: intl.formatMessage({ id: 'profile.activity.eventCreated' }) || 'Created event',
      details: 'Summer Concert 2025',
      ip: '192.168.1.1'
    },
    {
      date: '2025-05-03',
      action: intl.formatMessage({ id: 'profile.activity.galleryCreated' }) || 'Created gallery',
      details: 'VIP Photos',
      ip: '192.168.1.1'
    },
    {
      date: '2025-05-02',
      action: intl.formatMessage({ id: 'profile.activity.photosUploaded' }) || 'Uploaded photos',
      details: '35 photos to Summer Concert 2025',
      ip: '192.168.1.1'
    },
    {
      date: '2025-05-01',
      action: intl.formatMessage({ id: 'profile.activity.settingsChanged' }) || 'Changed settings',
      details: 'Profile information updated',
      ip: '192.168.1.1'
    }
  ];

  return (
    <StyledContent>
      {contextHolder}
      <StyledBreadcrumb>
        <Breadcrumb.Item>{user.organisation ? user.organisation.name : intl.formatMessage({ id: 'common.platformName' })}</Breadcrumb.Item>
        <Breadcrumb.Item><Link to="/dashboard/events"><FormattedMessage id={'section.eventsName'} /></Link></Breadcrumb.Item>
        <Breadcrumb.Item><FormattedMessage id={'section.profileName'} /></Breadcrumb.Item>
      </StyledBreadcrumb>
      
      <ContentWrapper>
        {/* Profile Header */}
        <Row gutter={24}>
          <Col xs={24} md={8} style={{ marginBottom: 24 }}>
            <ProfileCard>
              <StyledAvatar 
                size={100} 
                icon={<UserOutlined />}
              />
              <StyledTitle level={3}>{firstName} {lastName}</StyledTitle>
              
              <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 20 }}>
                <InfoText>
                  <MailOutlined />
                  {email}
                </InfoText>
                
                <InfoText>
                  <TeamOutlined />
                  {user?.organisation?.name || 'Organisation'}
                </InfoText>
                
                {user?.jobTitle && (
                  <InfoText>
                    <IdcardOutlined />
                    {user.jobTitle}
                  </InfoText>
                )}
              </Space>
              
              <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 20 }}>
                {getRoleNames().length > 0 && (
                  <div>
                    {getRoleNames().map((role: string, index: number) => (
                      <StyledTag key={index} color="blue">
                        {role}
                      </StyledTag>
                    ))}
                  </div>
                )}
                
                <Tag 
                  icon={<CheckCircleOutlined />} 
                  color={getStatusColor()}
                  style={{ margin: '0 auto', padding: '4px 12px', fontSize: '14px' }}
                >
                  {getStatusText()}
                </Tag>
              </Space>
              
              <StyledDivider />
              
              <Space style={{ marginTop: 'auto' }}>
                <StyledButton 
                  type="primary" 
                  icon={<EditOutlined />}
                  onClick={handleEditProfile}
                >
                  {intl.formatMessage({ id: 'profile.editProfile' }) || 'Edit Profile'}
                </StyledButton>
                
                <StyledButton 
                  type="default"
                  danger
                  icon={<LogoutOutlined />} 
                  onClick={handleSignOut}
                >
                  {intl.formatMessage({ id: 'profile.signOut' }) || 'Sign Out'}
                </StyledButton>
              </Space>
            </ProfileCard>
          </Col>
          
          <Col xs={24} md={16}>
            <StyledTabs defaultActiveKey="account">
              <TabPane 
                tab={
                  <span>
                    <UserOutlined />&nbsp;
                    {intl.formatMessage({ id: 'profile.tabs.account' }) || 'Account Information'}
                  </span>
                } 
                key="account"
              >
                <TabCard>
                  {editingProfile ? (
                    <StyledForm
                      form={profileForm}
                      layout="vertical"
                      onFinish={handleProfileSubmit}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="firstName"
                            label={intl.formatMessage({ id: 'profile.form.firstName' }) || 'First Name'}
                            rules={[{ required: true, message: intl.formatMessage({ id: 'profile.form.firstNameRequired' }) || 'Please enter your first name' }]}
                          >
                            <Input placeholder={intl.formatMessage({ id: 'profile.form.firstNamePlaceholder' }) || 'Enter your first name'} />
                          </Form.Item>
                        </Col>
                        
                        <Col span={12}>
                          <Form.Item
                            name="lastName"
                            label={intl.formatMessage({ id: 'profile.form.lastName' }) || 'Last Name'}
                            rules={[{ required: true, message: intl.formatMessage({ id: 'profile.form.lastNameRequired' }) || 'Please enter your last name' }]}
                          >
                            <Input placeholder={intl.formatMessage({ id: 'profile.form.lastNamePlaceholder' }) || 'Enter your last name'} />
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      <Form.Item
                        name="email"
                        label={intl.formatMessage({ id: 'profile.form.email' }) || 'Email'}
                        rules={[
                          { 
                            required: true, 
                            message: intl.formatMessage({ id: 'profile.form.emailRequired' }) || 'Please enter your email' 
                          },
                          { 
                            type: 'email', 
                            message: intl.formatMessage({ id: 'profile.form.emailValid' }) || 'Please enter a valid email' 
                          }
                        ]}
                      >
                        <Input 
                          placeholder={intl.formatMessage({ id: 'profile.form.emailPlaceholder' }) || 'Enter your email'}
                          disabled
                        />
                      </Form.Item>
                      
                      <Form.Item
                        name="jobTitle"
                        label={intl.formatMessage({ id: 'profile.form.jobTitle' }) || 'Job Title'}
                      >
                        <Input placeholder={intl.formatMessage({ id: 'profile.form.jobTitlePlaceholder' }) || 'Enter your job title'} />
                      </Form.Item>
                      
                      <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                          <StyledButton onClick={handleCancelEdit}>
                            {intl.formatMessage({ id: 'common.cancel' }) || 'Cancel'}
                          </StyledButton>
                          <StyledButton type="primary" htmlType="submit">
                            {intl.formatMessage({ id: 'common.save' }) || 'Save'}
                          </StyledButton>
                        </Space>
                      </Form.Item>
                    </StyledForm>
                  ) : (
                    <>
                      <SectionTitle level={4}>
                        {intl.formatMessage({ id: 'profile.personalInfo' }) || 'Personal Information'}
                      </SectionTitle>
                      
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Text type="secondary">
                            {intl.formatMessage({ id: 'profile.form.firstName' }) || 'First Name'}
                          </Text>
                          <Paragraph strong>{firstName}</Paragraph>
                        </Col>
                        
                        <Col span={12}>
                          <Text type="secondary">
                            {intl.formatMessage({ id: 'profile.form.lastName' }) || 'Last Name'}
                          </Text>
                          <Paragraph strong>{lastName}</Paragraph>
                        </Col>
                        
                        <Col span={12}>
                          <Text type="secondary">
                            {intl.formatMessage({ id: 'profile.form.email' }) || 'Email'}
                          </Text>
                          <Paragraph strong>{email}</Paragraph>
                        </Col>
                        
                        <Col span={12}>
                          <Text type="secondary">
                            {intl.formatMessage({ id: 'profile.form.jobTitle' }) || 'Job Title'}
                          </Text>
                          <Paragraph strong>{user?.jobTitle || '-'}</Paragraph>
                        </Col>
                      </Row>
                      
                      <StyledDivider />
                      
                      <SectionTitle level={4}>
                        {intl.formatMessage({ id: 'profile.passwordSecurity' }) || 'Password & Security'}
                      </SectionTitle>
                      
                      <Paragraph>
                        <StyledButton 
                          type="primary" 
                          icon={<LockOutlined />}
                          onClick={handleChangePassword}
                        >
                          {intl.formatMessage({ id: 'profile.changePassword' }) || 'Change Password'}
                        </StyledButton>
                      </Paragraph>
                      
                      <Paragraph type="secondary">
                        {intl.formatMessage({ id: 'profile.passwordLastChanged' }) || 'Password last changed:'} {new Date().toLocaleDateString()}
                      </Paragraph>
                    </>
                  )}
                </TabCard>
              </TabPane>
              
              <TabPane 
                tab={
                  <span>
                    <BellOutlined/>&nbsp;
                    {intl.formatMessage({ id: 'profile.tabs.notifications' }) || 'Notifications'}
                  </span>
                } 
                key="notifications"
              >
                <TabCard>
                  <SectionTitle level={4}>
                    {intl.formatMessage({ id: 'profile.notificationPreferences' }) || 'Notification Preferences'}
                  </SectionTitle>
                  
                  <StyledList
                    itemLayout="horizontal"
                    dataSource={notificationSettings}
                    renderItem={(item) => {
                      const typedItem = item as INotificationSetting;
                      return (
                        <List.Item
                          actions={[
                            <Switch defaultChecked={typedItem.defaultChecked} />
                          ]}
                        >
                          <List.Item.Meta
                            title={typedItem.title}
                            description={typedItem.description}
                          />
                        </List.Item>
                      );
                    }}
                  />
                </TabCard>
              </TabPane>
              
              <TabPane 
                tab={
                  <span>
                    <SafetyCertificateOutlined />&nbsp;
                    {intl.formatMessage({ id: 'profile.tabs.security' }) || 'Security'}
                  </span>
                } 
                key="security"
              >
                <TabCard>
                  <SectionTitle level={4}>
                    {intl.formatMessage({ id: 'profile.securitySettings' }) || 'Security Settings'}
                  </SectionTitle>
                  
                  <StyledList
                    itemLayout="horizontal"
                    dataSource={securitySettings}
                    renderItem={(item) => {
                      const typedItem = item as ISecuritySetting;
                      return (
                        <List.Item
                          actions={[
                            <Switch defaultChecked={typedItem.defaultChecked} />
                          ]}
                        >
                          <List.Item.Meta
                            title={typedItem.title}
                            description={typedItem.description}
                          />
                        </List.Item>
                      );
                    }}
                  />
                  
                  <StyledDivider />
                  
                  <SectionTitle level={4}>
                    {intl.formatMessage({ id: 'profile.connectedDevices' }) || 'Connected Devices'}
                  </SectionTitle>
                  
                  <StyledList
                    itemLayout="horizontal"
                    dataSource={[
                      {
                        name: 'Chrome on Windows',
                        lastActive: 'Now',
                        current: true
                      } as IDeviceInfo
                    ]}
                    renderItem={(item) => {
                      const typedItem = item as IDeviceInfo;
                      return (
                        <List.Item
                          actions={[
                            typedItem.current ? (
                              <Tag color="success">
                                {intl.formatMessage({ id: 'profile.currentDevice' }) || 'Current Device'}
                              </Tag>
                            ) : (
                              <Button danger size="small" style={{ borderRadius: '6px' }}>
                                {intl.formatMessage({ id: 'profile.removeDevice' }) || 'Remove'}
                              </Button>
                            )
                          ]}
                        >
                          <List.Item.Meta
                            title={typedItem.name}
                            description={`${intl.formatMessage({ id: 'profile.lastActive' }) || 'Last active'}: ${typedItem.lastActive}`}
                          />
                        </List.Item>
                      );
                    }}
                  />
                </TabCard>
              </TabPane>
              
              <TabPane 
                tab={
                  <span>
                    <HistoryOutlined />&nbsp;
                    {intl.formatMessage({ id: 'profile.tabs.activity' }) || 'Activity'}
                  </span>
                } 
                key="activity"
              >
                <TabCard>
                  <SectionTitle level={4}>
                    {intl.formatMessage({ id: 'profile.recentActivity' }) || 'Recent Activity'}
                  </SectionTitle>
                  
                  <StyledList
                    itemLayout="horizontal"
                    dataSource={activityLogs}
                    renderItem={(item) => {
                      const typedItem = item as IActivityLog;
                      return (
                        <List.Item>
                          <List.Item.Meta
                            title={<ActivityItem><div className="activity-action">{typedItem.action}</div></ActivityItem>}
                            description={
                              <ActivityItem>
                                <div className="activity-details">
                                  <div>{typedItem.details}</div>
                                  <div>{intl.formatMessage({ id: 'profile.activity.date' }) || 'Date'}: {typedItem.date}</div>
                                  <div>IP: {typedItem.ip}</div>
                                </div>
                              </ActivityItem>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                </TabCard>
              </TabPane>
            </StyledTabs>
          </Col>
        </Row>
      </ContentWrapper>
      
      {/* Password Change Modal */}
      <StyledModal
        title={intl.formatMessage({ id: 'profile.changePassword' }) || 'Change Password'}
        open={changingPassword}
        onCancel={handleCancelPasswordChange}
        footer={null}
      >
        <StyledForm
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSubmit}
        >
          <Form.Item
            name="currentPassword"
            label={intl.formatMessage({ id: 'profile.form.currentPassword' }) || 'Current Password'}
            rules={[{ required: true, message: intl.formatMessage({ id: 'profile.form.currentPasswordRequired' }) || 'Please enter your current password' }]}
          >
            <Input.Password placeholder={intl.formatMessage({ id: 'profile.form.currentPasswordPlaceholder' }) || 'Enter your current password'} />
          </Form.Item>
          
          <Form.Item
            name="newPassword"
            label={intl.formatMessage({ id: 'profile.form.newPassword' }) || 'New Password'}
            rules={[
              { 
                required: true, 
                message: intl.formatMessage({ id: 'profile.form.newPasswordRequired' }) || 'Please enter your new password' 
              },
              { 
                min: 8, 
                message: intl.formatMessage({ id: 'profile.form.passwordLength' }) || 'Password must be at least 8 characters' 
              }
            ]}
          >
            <Input.Password placeholder={intl.formatMessage({ id: 'profile.form.newPasswordPlaceholder' }) || 'Enter your new password'} />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label={intl.formatMessage({ id: 'profile.form.confirmPassword' }) || 'Confirm New Password'}
            dependencies={['newPassword']}
            rules={[
              { 
                required: true, 
                message: intl.formatMessage({ id: 'profile.form.confirmPasswordRequired' }) || 'Please confirm your new password' 
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(intl.formatMessage({ id: 'profile.form.passwordMismatch' }) || 'The two passwords do not match')
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder={intl.formatMessage({ id: 'profile.form.confirmPasswordPlaceholder' }) || 'Confirm your new password'} />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <StyledButton onClick={handleCancelPasswordChange}>
                {intl.formatMessage({ id: 'common.cancel' }) || 'Cancel'}
              </StyledButton>
              <StyledButton type="primary" htmlType="submit">
                {intl.formatMessage({ id: 'common.save' }) || 'Save'}
              </StyledButton>
            </Space>
          </Form.Item>
        </StyledForm>
      </StyledModal>
    </StyledContent>
  );
}

export default ProfilePage;